import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';

const ForgotPasswordPage: React.FC = () => {
  const { forgotPassword, isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();
  const [step, setStep] = useState<'request' | 'success'>('request');
  const [identifier, setIdentifier] = useState('');
  const [identifierType, setIdentifierType] = useState<'phone' | 'email'>('phone');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!identifier.trim()) {
      return;
    }

    const resetData = identifierType === 'email' 
      ? { email: identifier.trim() }
      : { phone: identifier.trim() };

    const result = await forgotPassword(resetData);
    
    if (result.success) {
      setStep('success');
    }
  };

  const handleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setIdentifier(value);
    
    // Auto-detect if it's a phone number or email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[\d\s\-\(\)]+$/;
    
    if (emailRegex.test(value)) {
      setIdentifierType('email');
    } else if (phoneRegex.test(value) && value.length >= 10) {
      setIdentifierType('phone');
    }
  };

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {step === 'request' ? t('forgotPassword') : t('requestSubmitted')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {step === 'request' ? t('enterIdentifierForReset') : t('adminWillContactYou')}
          </p>
        </div>
        
        {step === 'request' ? (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="identifier" className="block text-sm font-medium text-gray-700">
                  {identifierType === 'email' ? t('emailAddress') : t('phoneNumber')}
                </label>
                <input
                  id="identifier"
                  name="identifier"
                  type={identifierType === 'email' ? 'email' : 'tel'}
                  autoComplete={identifierType === 'email' ? 'email' : 'tel'}
                  required
                  className="input-field mt-1"
                  placeholder={identifierType === 'email' ? t('enterEmail') : t('enterPhone')}
                  value={identifier}
                  onChange={handleIdentifierChange}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('enterIdentifierForPasswordReset')}
                </p>
              </div>

              {/* Email option toggle */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setIdentifierType(identifierType === 'phone' ? 'email' : 'phone');
                    setIdentifier('');
                  }}
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  {identifierType === 'phone' ? t('useEmailInstead') : t('usePhoneInstead')}
                </button>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    {t('importantNotice')}
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>{t('passwordResetRequiresAdmin')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading || !identifier.trim()}
                className="btn-primary w-full flex justify-center py-2 px-4"
              >
                {isLoading ? t('submitting') : t('submitRequest')}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                {t('rememberPassword')}{' '}
                <Link
                  to="/login"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  {t('signInHere')}
                </Link>
              </p>
            </div>
          </form>
        ) : (
          <div className="mt-8 space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    {t('requestSubmitted')}
                  </h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>{t('adminWillReviewRequest')}</p>
                    <p className="mt-1">{t('youWillBeContacted')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center space-y-4">
              <Link
                to="/login"
                className="btn-primary w-full flex justify-center py-2 px-4"
              >
                {t('backToLogin')}
              </Link>
              
              <button
                type="button"
                onClick={() => setStep('request')}
                className="w-full text-sm text-gray-600 hover:text-gray-500"
              >
                {t('submitAnotherRequest')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage; 